import { useEffect, useState } from "react";
import {
  subscribeItdrAlerts,
  formatItdrPayloadSummary,
  type ItdrAlertEventDto,
} from "../../lib/itdr-alerts";
import { getTenantExternalId } from "../../lib/tenant-settings";

const MAX_TOASTS = 8;

const severityColor: Record<string, string> = {
  low: "border-card bg-app",
  medium: "border-blue-400 bg-blue-50 dark:bg-blue-900/20",
  high: "border-amber-500 bg-amber-50 dark:bg-amber-900/20",
  critical: "border-rose-500 bg-rose-50 dark:bg-rose-900/20",
};

const channelLabel: Record<string, string> = {
  "itdr.honeypot": "Honeypot",
  "itdr.decoy": "Decoy",
  "itdr.session_analysis": "Session analysis",
  "itdr.lateral_movement": "Lateral movement",
  "itdr.network_signal": "Network / protocol ITDR",
};

/**
 * Phase 6 (OOS) — global toaster wired to the ITDR alert SSE stream.
 *
 * Renders a stack of up to MAX_TOASTS recent alerts in the bottom-right corner.
 * Mount once in the app shell.
 */
export function ItdrAlertToaster() {
  const tenantExternalId = getTenantExternalId();
  const [events, setEvents] = useState<ItdrAlertEventDto[]>([]);

  useEffect(() => {
    if (!tenantExternalId) return;
    const sub = subscribeItdrAlerts(tenantExternalId, (event) => {
      setEvents((prev) => [event, ...prev].slice(0, MAX_TOASTS));
    });
    return () => sub.close();
  }, [tenantExternalId]);

  if (!events.length) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-[360px]">
      {events.map((ev) => (
        <div
          key={`${ev.channel}-${ev.emittedAt}`}
          className={`rounded-md border p-3 shadow text-xs ${
            severityColor[ev.severity] ?? severityColor.medium
          }`}
        >
          <div className="font-medium uppercase tracking-wide">
            {channelLabel[ev.channel] ?? ev.channel.replace("itdr.", "")} · {ev.severity}
          </div>
          <div className="mt-1 break-words">
            {ev.trigger} — {formatItdrPayloadSummary(ev.payload)}
          </div>
          {ev.mitre ? (
            <div className="mt-1.5 space-y-0.5 text-[10px] text-muted">
              <div className="font-mono">
                MITRE {ev.mitre.techniqueId} · {ev.mitre.techniqueName}
              </div>
              <div>{ev.mitre.recommendedResponse}</div>
            </div>
          ) : null}
          {typeof ev.payload?.threatIntelMatch === "object" &&
          ev.payload.threatIntelMatch !== null ? (
            <div className="mt-1 text-[10px] text-rose-700 dark:text-rose-300">
              IOC match: known threat IP (score{" "}
              {String(
                (ev.payload.threatIntelMatch as { threatScore?: unknown }).threatScore ?? "?"
              )}
              )
            </div>
          ) : null}
          <div className="mt-1 text-[10px] text-subtle">{ev.emittedAt}</div>
          <button
            type="button"
            className="mt-2 text-[10px] underline"
            onClick={() =>
              setEvents((prev) =>
                prev.filter((e) => e.emittedAt !== ev.emittedAt || e.channel !== ev.channel)
              )
            }
          >
            dismiss
          </button>
        </div>
      ))}
    </div>
  );
}
